import React from 'react';
import { FaEye, FaPrint, FaEdit, FaTrash, FaUndo, FaCloudUploadAlt, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { formatDate } from '../../../utils/formatters';
import { useTranslation } from '../../../context/LanguageContext';

const OrdersTable = ({
    orders,
    loading,
    formatCurrency,
    isAdmin,
    canUploadZatca,
    onViewOrder,
    onPrintInvoice,
    onEditOrder,
    onDeleteOrder,
    onReturnOrder,
    onUploadZatca
}) => {
    const { t, language } = useTranslation();
    if (loading) {
        return (
            <div className="flex justify-center items-center h-48">
                <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-yellow-500"></div>
            </div>
        );
    }

    if (orders.length === 0) {
        return (
            <div className="text-center py-12">
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                    {t('admin.no_orders')}
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                    {t('admin.no_orders_found')}
                </p>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-xl shadow-lg w-full">
            <div className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
                <table className="w-full min-w-[560px] divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className={`px-1.5 sm:px-2 lg:px-3 py-2 ${language === 'ar' ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[110px]`}>
                                {t('admin.order_number')}
                            </th>
                            <th className={`px-1.5 sm:px-2 lg:px-3 py-2 ${language === 'ar' ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[110px]`}>
                                {t('admin.date')}
                            </th>
                            <th className={`px-1.5 sm:px-2 lg:px-3 py-2 ${language === 'ar' ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[110px]`}>
                                {t('admin.customer')}
                            </th>
                            
                            <th className={`px-1.5 sm:px-2 lg:px-3 py-2 ${language === 'ar' ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[90px]`}>
                                {t('admin.filters.order_type')}
                            </th>

                            <th className={`px-1.5 sm:px-2 lg:px-3 py-2 ${language === 'ar' ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[90px]`}>
                                {t('admin.total')}
                            </th>
                            <th className={`px-1.5 sm:px-2 lg:px-3 py-2 ${language === 'ar' ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[90px]`}>
                                {t('admin.status')}
                            </th>
                            <th className={`px-1.5 sm:px-2 lg:px-3 py-2 ${language === 'ar' ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[90px]`}>
                            {t('admin.uploaded')}
                            </th>
                            <th className={`px-1.5 sm:px-2 lg:px-3 py-2 ${language === 'ar' ? 'text-left' : 'text-right'} text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px] sticky right-0 bg-gray-50`}>
                                {t('admin.actions')}
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {orders.map((order) => (
                            <tr key={order.unique_id || `${order.payment_method}-${order.id}`} className="hover:bg-gray-50 transition-colors duration-200">
                                <td className={`px-1.5 sm:px-2 lg:px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                    <span className="font-mono text-xs sm:text-sm">#{order.order_number}</span>
                                </td>
                                <td className={`px-1.5 sm:px-2 lg:px-3 py-2 whitespace-nowrap text-xs sm:text-sm text-gray-500 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                    <div className="truncate max-w-[120px] sm:max-w-none">
                                        {formatDate(order.created_at, language)}
                                    </div>
                                </td>
                                <td className={`px-1.5 sm:px-2 lg:px-3 py-2 text-xs sm:text-sm text-gray-900 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                    <div className="max-w-[120px] sm:max-w-none">
                                        <div className="truncate font-medium">
                                            {order.customer_name || t('admin.walk_in_customer')}
                                        </div>
                                        {order.customer_phone && (
                                            <div className="text-gray-500 text-xs truncate">{order.customer_phone}</div>
                                        )}
                                    </div>
                                </td>
                                
                                <td className={`px-1.5 sm:px-2 lg:px-3 py-2 whitespace-nowrap text-sm ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                    {order.is_card_order ? (
                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                            {t('admin.filters.card')}
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                            {t('admin.filters.cash')}
                                        </span>
                                    )}
                                </td>

                                <td className={`px-1.5 sm:px-2 lg:px-3 py-2 whitespace-nowrap text-xs sm:text-sm font-semibold text-gray-900 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                    {formatCurrency(order.total)}
                                </td>
                                <td className={`px-1.5 sm:px-2 lg:px-3 py-2 whitespace-nowrap text-sm ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                    {order.status === 'returned' ? (
                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                            {t('admin.returned')}
                                        </span>
                                    ) : (
                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                            {t('admin.active')}
                                        </span>
                                    )}
                                </td>
                                <td className={`px-1.5 sm:px-2 lg:px-3 py-2 whitespace-nowrap ${language === 'ar' ? 'text-right' : 'text-left'} text-sm font-medium`}>
                                    {order.is_zatca_uploaded ? (
                                        <span className="text-green-600" title="Uploaded to ZATCA">
                                            <FaCheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                                        </span>
                                    ) : (
                                        <span className="text-red-500" title="Not uploaded to ZATCA">
                                            <FaTimesCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                                        </span>
                                    )}
                                </td>
                                <td className={`px-1.5 sm:px-2 lg:px-3 py-2 whitespace-nowrap ${language === 'ar' ? 'text-left' : 'text-right'} text-sm font-medium sticky right-0 bg-white hover:bg-gray-50 transition-colors duration-200`}>
                                    <div className="flex items-center justify-end gap-0.5">
                                        <button
                                            onClick={() => onViewOrder(order)}
                                            className="btn-compact text-yellow-600 hover:text-yellow-900 p-0.5 sm:p-1 rounded hover:bg-yellow-50/70 transition-all duration-200"
                                            title={t('admin.view_order')}
                                        >
                                            <FaEye className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => onPrintInvoice(order)}
                                            className="btn-compact text-yellow-600 hover:text-yellow-900 p-0.5 sm:p-1 rounded hover:bg-yellow-50/70 transition-all duration-200"
                                            title={t('admin.print_invoice')}
                                        >
                                            <FaPrint className="w-4 h-4" />
                                        </button>
                                        {/* ZATCA upload action */}
                                        {!order.is_zatca_uploaded && canUploadZatca && (
                                            <button
                                                onClick={() => onUploadZatca(order)}
                                                className="btn-compact text-blue-600 hover:text-blue-900 p-0.5 sm:p-1 rounded hover:bg-blue-50/70 transition-all duration-200"
                                                title="Upload to ZATCA"
                                            >
                                                <FaCloudUploadAlt className="w-4 h-4" />
                                            </button>
                                        )}
                                        {isAdmin && (
                                            <>
                                                <button
                                                    onClick={() => onEditOrder(order)}
                                                    className="btn-compact text-yellow-600 hover:text-yellow-900 p-0.5 sm:p-1 rounded hover:bg-yellow-50/70 transition-all duration-200"
                                                    title={t('admin.edit_order')}
                                                >
                                                    <FaEdit className="w-4 h-4" />
                                                </button>
                                                {!order.status && (
                                                    <button
                                                        onClick={() => onReturnOrder(order)}
                                                        className="btn-compact text-yellow-600 hover:text-yellow-900 p-0.5 sm:p-1 rounded hover:bg-yellow-50/70 transition-all duration-200"
                                                        title={t('admin.return_order')}
                                                    >
                                                        <FaUndo className="w-4 h-4" />
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => onDeleteOrder(order)}
                                                    className="btn-compact text-red-600 hover:text-red-900 p-0.5 sm:p-1 rounded hover:bg-red-50/70 transition-all duration-200"
                                                    title={t('admin.delete_order')}
                                                >
                                                    <FaTrash className="w-4 h-4" />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default OrdersTable;
